import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { getSites, setSiteIdGetter } from "../lib/api";
import { useOrgId } from "./useOrgId";

const SiteContext = createContext(null);
const storageKey = (orgId) => `whyrank:site:${orgId}`;

function readStored(orgId) {
  try {
    return localStorage.getItem(storageKey(orgId));
  } catch {
    return null;
  }
}

function writeStored(orgId, siteId) {
  try {
    localStorage.setItem(storageKey(orgId), siteId);
  } catch {
    // private window / blocked storage: the choice just isn't remembered
  }
}

// Active site of the active org. Every API call sends it as X-Site-Id; children render only
// once sites are loaded, because GET /sites also creates the org's primary site on first use.
export function SiteProvider({ children }) {
  const orgId = useOrgId();
  const [state, setState] = useState({ orgId: null, sites: [], maxSites: 1, siteId: null, error: null });

  const load = useCallback(async () => {
    try {
      const { sites, max_sites } = await getSites();
      const stored = readStored(orgId);
      const siteId = sites.some((s) => s.id === stored) ? stored : sites[0]?.id || null;
      setState({ orgId, sites, maxSites: max_sites, siteId, error: null });
    } catch (e) {
      console.error("[sites] load failed:", e);
      setState({ orgId, sites: [], maxSites: 1, siteId: null, error: e });
    }
  }, [orgId]);

  useEffect(() => {
    if (orgId) load();
  }, [orgId, load]);

  const switchSite = useCallback((siteId) => {
    writeStored(orgId, siteId);
    setState((s) => ({ ...s, siteId }));
  }, [orgId]);

  if (state.orgId !== orgId) return null; // first load for this org

  // Set during render, not in an effect: child effects run before parent effects, and the
  // pages' first fetches must already carry the active site.
  const activeSiteId = state.siteId;
  setSiteIdGetter(() => activeSiteId);

  const site = state.sites.find((s) => s.id === state.siteId) || null;
  return (
    <SiteContext.Provider value={{ ...state, site, switchSite, reload: load }}>
      {children}
    </SiteContext.Provider>
  );
}

export function useSite() {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error("useSite must be used within SiteProvider");
  return ctx;
}
